import {DataTableColumn} from 'mantine-datatable';
import {NavLink} from 'react-router-dom';
import {I18n} from '@aws-amplify/core';

import {formatTimestamp} from '../../../utils/TableTimestamps';
import {Account, AccountStatus} from '../types';
import {AccountStatusBadge} from '../profile/AccountStatusBadge';
import {AccountTypeBadge} from '../profile/AccountTypeBadge';

// FNP-383: days remaining in the 30-day soft-delete retention window.
// Computed from scheduledHardDeleteAt (set by the FNP-281 soft-delete
// resolver) minus now. Returns null on non-PENDING_DELETION rows or when
// the field is missing. Clamped at 0 so an already-overdue row reads "0"
// rather than a negative.
export const daysRemaining = (item: Account): number | null => {
    if (item.accountStatus !== AccountStatus.PENDING_DELETION) {
        return null;
    }
    if (!item.scheduledHardDeleteAt) {
        return null;
    }
    const scheduled = new Date(item.scheduledHardDeleteAt).getTime();
    if (Number.isNaN(scheduled)) {
        return null;
    }
    const diffMs = scheduled - Date.now();
    if (diffMs <= 0) {
        return 0;
    }
    return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
};

// FNP-555: moved from tables/columns.tsx and given an `accessor` per column.
// `render` controls how a cell LOOKS; `accessor` is what sorting sorts BY, so a
// render-only config made §3a sorting structurally impossible.
export const useAccountsTableColumns = () => {

    const columns: DataTableColumn<Account>[] = [
        {
            accessor: 'email',
            title: I18n.get('Account'),
            sortable: true,
            width: '1fr',
            render: (item: Account) => <NavLink to={`/accounts/${item.id}`}>{item.email}</NavLink>,
        },
        {
            accessor: 'sub',
            title: I18n.get('SUB'),
            sortable: true,
            width: '1fr',
            render: (item: Account) => item.sub,
        },
        {
            accessor: 'username',
            title: I18n.get('Username'),
            sortable: true,
            width: '1fr',
            render: (item: Account) => item.username,
        },
        {
            accessor: 'accountType',
            title: I18n.get('Account Type'),
            sortable: true,
            width: '1fr',
            render: (item: Account) => <AccountTypeBadge record={item}/>,
        },
        {
            accessor: 'accountStatus',
            title: I18n.get('Status'),
            sortable: true,
            width: '1fr',
            render: (item: Account) => <AccountStatusBadge record={item}/>,
        },
        {
            // Sorts by the underlying retention deadline, not by the rendered
            // day count: both orders agree (a nearer deadline is fewer days),
            // and scheduledHardDeleteAt is null on every non-PENDING_DELETION
            // row, so those sort last in both directions rather than
            // competing as zero.
            accessor: 'scheduledHardDeleteAt',
            title: I18n.get('Days Remaining'),
            sortable: true,
            width: '1fr',
            render: (item: Account) => {
                const days = daysRemaining(item);
                return days === null ? '' : `${days}`;
            },
        },
        {
            accessor: 'created',
            title: I18n.get('Created'),
            sortable: true,
            width: '1fr',
            render: (item: Account) => formatTimestamp(item.created),
        },
    ];

    return {
        columns,
    };
};
